import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { Carousel } from '@leagueplatform/web-common-components';
import { LinkLegacy as Link } from '@leagueplatform/routing';
import { handleStaticAsset } from '@leagueplatform/asset-config';
import { Achievement, REWARDS_EVENTS_ASSET_MAP } from '#constants/types.ts';

import { track } from '@leagueplatform/analytics';
import {
  Box,
  Button,
  Modal,
  StackLayout,
  VisuallyHidden,
} from '@leagueplatform/genesis-core';

import { useIntl } from '@leagueplatform/locales';
import { useConfigProperty } from '@leagueplatform/core';
import { CelebrationContent } from '../celebration-content/celebration-content.view';

interface CelebrationModalProps {
  celebrations: Achievement[];
  onClose: () => void;
}

export const CelebrationModal = ({
  celebrations,
  onClose,
}: CelebrationModalProps) => {
  const { formatMessage } = useIntl();
  const disableAchievementsPageLinks = useConfigProperty(
    'rewardsEvents.disableAchievementsPageLinks',
    false,
  );

  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [showAnimation, setShowAnimation] = useState(false);
  const confettiAnimationRef = useRef(null);

  useEffect(() => {
    setTimeout(() => {
      setShowAnimation(true);
    }, 2100);
  }, []);

  useEffect(() => {
    track('rewards.screen_loaded.celebration_modal');
  }, [celebrations]);

  const handleCloseClick = () => {
    track('rewards.button_clicked.celebration_modal.close');
    onClose();
  };

  return (
    <Modal.Root
      defaultOpen
      onOpenChange={(open) => {
        if (!open) handleCloseClick();
      }}
    >
      <Modal.Content
        css={{
          '.GDS-modal': { width: '100%', maxWidth: '848px' },
        }}
      >
        <Modal.Title>
          <VisuallyHidden>
            {formatMessage({ id: 'ACHIEVEMENT_EARNED' })}
          </VisuallyHidden>
        </Modal.Title>
        <StackLayout
          orientation="vertical"
          css={{
            position: 'relative',
            backgroundColor: 'transparent',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Box css={{ position: 'absolute', top: 0, height: 300 }}>
            {
              handleStaticAsset(
                REWARDS_EVENTS_ASSET_MAP.ACHIEVEMENT_CONFETTI_ANIMATION,
                {
                  isComponent: true,
                  props: {
                    animationRef: confettiAnimationRef,
                    shouldPlay: showAnimation,
                  },
                },
              ) as ReactNode
            }
          </Box>
          <Box css={{ maxWidth: '384px', position: 'relative' }}>
            <Carousel
              sideArrows
              showProgressDots
              name="celebration-modal"
              onIndexChange={(index: number) => setCurrentCarouselIndex(index)}
            >
              {celebrations.map((achievement, index) => (
                <CelebrationContent
                  index={index}
                  isVisible={index === currentCarouselIndex}
                  achievement={achievement}
                />
              ))}
            </Carousel>
          </Box>
          {!disableAchievementsPageLinks && (
            <StackLayout
              spacing="$oneAndHalf"
              horizontalAlignment="center"
              orientation="horizontal"
              css={{
                width: '100%',
                flexWrap: 'wrap-reverse',
                marginTop: '$three',
              }}
            >
              {celebrations[currentCarouselIndex]
                .secondary_cta_destination_url && (
                <Box>
                  <Button
                    priority="secondary"
                    as={Link}
                    to={
                      celebrations[currentCarouselIndex]
                        .secondary_cta_destination_url || '/#'
                    }
                    quiet
                    onClick={onClose}
                  >
                    {celebrations[currentCarouselIndex].secondary_cta_title}
                  </Button>
                </Box>
              )}
              {celebrations[currentCarouselIndex]
                .primary_cta_destination_url && (
                <Box>
                  <Button
                    as={Link}
                    to={
                      celebrations[currentCarouselIndex]
                        .primary_cta_destination_url || '/#'
                    }
                    priority="primary"
                    role="link"
                    onClick={onClose}
                    css={{ textDecoration: 'none' }}
                  >
                    {celebrations[currentCarouselIndex].primary_cta_title}
                  </Button>
                </Box>
              )}
            </StackLayout>
          )}
        </StackLayout>
      </Modal.Content>
    </Modal.Root>
  );
};
